import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { Plus, MoreHorizontal, Trash2, Wrench, Users, Box, MapPin, Building2, Briefcase, RotateCcw, Award, ShieldCheck, Pencil, FileText, Stamp, History } from 'lucide-react';
import { AuditTrailPanel, LastModifiedBadge } from '@/components/AuditTrailPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const TAB_CONFIG = [
  { key: 'categories', label: 'Categories', apiType: 'equipment_category', placeholder: 'e.g., Heavy Machinery, Cranes', entityName: 'Category', group: 'equipment', icon: Wrench },
  { key: 'subcategories', label: 'Subcategories', apiType: 'equipment_subcategory', placeholder: 'e.g., Excavator, Tower Crane', entityName: 'Subcategory', group: 'equipment', icon: Box, hasParentCategory: true },
  { key: 'bundle_types', label: 'Bundle Types', apiType: 'bundle_type', placeholder: 'e.g., Loose, Container, Assembly', entityName: 'Bundle Type', group: 'equipment', icon: Box },
  { key: 'locations', label: 'Locations', apiType: 'location', placeholder: 'e.g., Australia, Middle East', entityName: 'Location', group: 'equipment', icon: MapPin },
  { key: 'ownership', label: 'Ownership Entities', apiType: 'ownership_entity', placeholder: 'e.g., Fagioli SPA', entityName: 'Ownership Entity', group: 'equipment', icon: Building2 },
  { key: 'roles', label: 'Roles', apiType: 'personnel_role', placeholder: 'e.g., Site Engineer, Crane Operator', entityName: 'Role', group: 'personnel', icon: Briefcase },
  { key: 'departments', label: 'Departments', apiType: 'department', placeholder: 'e.g., Operations, Engineering, Finance', entityName: 'Department', group: 'personnel', icon: Building2 },
  { key: 'contract_types', label: 'Contract Types', apiType: 'contract_type', placeholder: 'e.g., Permanent, Fixed-Term, Project-Based', entityName: 'Contract Type', group: 'personnel', icon: FileText },
  { key: 'visa_types', label: 'Visa Types', apiType: 'visa_type', placeholder: 'e.g., Work Visa, 482 Visa, Citizen', entityName: 'Visa Type', group: 'personnel', icon: Stamp },
  { key: 'rotations', label: 'Rotations', apiType: 'rotation', placeholder: 'e.g., 2:1 (2 weeks on / 1 week off)', entityName: 'Rotation', group: 'personnel', icon: RotateCcw, hasExtra: true },
  { key: 'cert_types', label: 'Certifications', apiType: 'certification_type', placeholder: 'e.g., White Card, Working at Heights', entityName: 'Certification Type', group: 'personnel', icon: Award },
  { key: 'induction_types', label: 'Inductions', apiType: 'induction_type', placeholder: 'e.g., PPA Induction, Qube Induction', entityName: 'Induction Type', group: 'personnel', icon: ShieldCheck },
  { key: 'project_statuses', label: 'Project Statuses', apiType: 'project_status', placeholder: 'e.g., Secured, Likely, Prospect', entityName: 'Project Status', group: 'projects', icon: Briefcase },
  { key: 'project_stages', label: 'Project Stages', apiType: 'project_stage', placeholder: 'e.g., Operational, Upcoming, Completed', entityName: 'Project Stage', group: 'projects', icon: Award },
];

export default function DataManagement({ user }) {
  const notification = useNotification();
  const { canView } = usePermissions();
  const [data, setData] = useState({});
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('categories');
  const [formData, setFormData] = useState({ name: '', type: 'equipment_category', parent_category: '', days_on: '', days_off: '' });
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    TAB_CONFIG.forEach(tab => fetchData(tab.key, tab.apiType));
  }, []);

  const fetchData = async (key, apiType) => {
    try {
      const response = await axios.get(`${API}/categories?type=${apiType}`, { headers: getAuthHeader() });
      setData(prev => ({ ...prev, [key]: response.data }));
    } catch (error) { /* silently fail */ }
  };

  const getTabConfig = () => TAB_CONFIG.find(t => t.key === activeTab) || TAB_CONFIG[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const tab = getTabConfig();
      const submitData = { name: formData.name, type: tab.apiType };
      if (tab.hasParentCategory && formData.parent_category) {
        submitData.parent_category = formData.parent_category;
      }
      if (tab.hasExtra) {
        if (formData.days_on) submitData.days_on = parseInt(formData.days_on);
        if (formData.days_off) submitData.days_off = parseInt(formData.days_off);
      }
      if (editingItem) {
        await axios.put(`${API}/categories/${editingItem.id}`, submitData, { headers: getAuthHeader() });
        notification.success(`${tab.entityName} updated successfully`);
      } else {
        await axios.post(`${API}/categories`, submitData, { headers: getAuthHeader() });
        notification.success(`${tab.entityName} created successfully`);
      }
      setOpen(false);
      setEditingItem(null);
      fetchData(activeTab, tab.apiType);
      resetForm();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item, tabKey) => {
    const tab = TAB_CONFIG.find(t => t.key === tabKey);
    setEditingItem(item);
    setFormData({ name: item.name, type: tab.apiType, parent_category: item.parent_category || '', days_on: item.days_on || '', days_off: item.days_off || '' });
    setActiveTab(tabKey);
    setOpen(true);
  };

  const handleRowClick = (e, item, tabKey) => {
    if (e.target.closest('[data-actions-menu]')) return;
    handleEdit(item, tabKey);
  };

  const handleDelete = (id, tabKey) => {
    const tab = TAB_CONFIG.find(t => t.key === tabKey);
    notification.confirm(`Are you sure you want to delete this ${tab.entityName.toLowerCase()}?`, async () => {
      try {
        await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeader() });
        notification.success('Deleted successfully');
        fetchData(tabKey, tab.apiType);
      } catch (error) { notification.error('Failed to delete'); }
    });
  };

  const resetForm = () => {
    const tab = getTabConfig();
    setFormData({ name: '', type: tab.apiType, parent_category: '', days_on: '', days_off: '' });
    setEditingItem(null);
  };

  const openAddSheet = (tabKey) => {
    const tab = TAB_CONFIG.find(t => t.key === tabKey);
    setActiveTab(tabKey);
    setEditingItem(null);
    setFormData({ name: '', type: tab.apiType, parent_category: '', days_on: '', days_off: '' });
    setOpen(true);
  };

  if (!canView('data_management')) {
    return (
      <div className="p-6 md:p-8 lg:p-12">
        <div className="text-center py-12"><p className="text-lg text-slate-600">Access denied. Insufficient permissions.</p></div>
      </div>
    );
  }

  const pluralize = (name) => {
    if (name.endsWith('s')) return name + 'es';
    if (name.endsWith('y') && !name.endsWith('ey')) return name.slice(0, -1) + 'ies';
    return name + 's';
  };

  const activeConfig = getTabConfig();
  const items = data[activeTab] || [];
  const equipmentTabs = TAB_CONFIG.filter(t => t.group === 'equipment');
  const personnelTabs = TAB_CONFIG.filter(t => t.group === 'personnel');
  const projectsTabs = TAB_CONFIG.filter(t => t.group === 'projects');

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="data-management-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Data Management</h1>
          <p className="text-sm text-slate-600 mt-2">Configure dropdown options and master data for your organization</p>
        </div>
        <Button data-testid="dm-audit-trail-button" variant="outline" className="border-slate-300 px-4 py-2 font-medium" onClick={() => setAuditOpen(true)}>
          <History className="w-4 h-4 mr-2" />Activity Log
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Left Sidebar Navigation */}
        <div className="w-56 shrink-0" data-testid="data-nav-sidebar">
          <nav className="space-y-6">
            {/* Equipment Section */}
            <div>
              <div className="flex items-center gap-2 px-3 mb-2">
                <Wrench className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Equipment</span>
              </div>
              <div className="space-y-0.5">
                {equipmentTabs.map(tab => {
                  const Icon = tab.icon;
                  const count = (data[tab.key] || []).length;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`nav-${tab.key}`}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'bg-slate-900 text-white font-medium'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{tab.label}</span>
                      </div>
                      <span className={`text-xs tabular-nums ${
                        activeTab === tab.key ? 'text-slate-400' : 'text-slate-400'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Personnel Section */}
            <div>
              <div className="flex items-center gap-2 px-3 mb-2">
                <Users className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Personnel</span>
              </div>
              <div className="space-y-0.5">
                {personnelTabs.map(tab => {
                  const Icon = tab.icon;
                  const count = (data[tab.key] || []).length;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`nav-${tab.key}`}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'bg-slate-900 text-white font-medium'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{tab.label}</span>
                      </div>
                      <span className={`text-xs tabular-nums ${
                        activeTab === tab.key ? 'text-slate-400' : 'text-slate-400'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Projects Section */}
            <div>
              <div className="flex items-center gap-2 px-3 mb-2">
                <Briefcase className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Projects</span>
              </div>
              <div className="space-y-0.5">
                {projectsTabs.map(tab => {
                  const Icon = tab.icon;
                  const count = (data[tab.key] || []).length;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`nav-${tab.key}`}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'bg-slate-900 text-white font-medium'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{tab.label}</span>
                      </div>
                      <span className={`text-xs tabular-nums ${
                        activeTab === tab.key ? 'text-slate-400' : 'text-slate-400'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          <Card className="bg-white border-slate-200 shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-semibold text-slate-900" data-testid="active-section-title">
                  {pluralize(activeConfig.entityName)}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {items.length} {items.length === 1 ? 'entry' : 'entries'}
                </p>
              </div>
              <Sheet open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setEditingItem(null); }}>
                <SheetTrigger asChild>
                  <Button
                    onClick={() => openAddSheet(activeTab)}
                    data-testid={`add-${activeTab}-button`}
                    className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm rounded-md px-5 py-2 font-medium"
                  >
                    <Plus className="w-4 h-4 mr-2" />Add {activeConfig.entityName}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md lg:max-w-lg overflow-y-auto" data-testid={`${activeTab}-sheet`}>
                  <SheetHeader>
                    <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
                      {editingItem ? `Edit ${activeConfig.entityName}` : `Add ${activeConfig.entityName}`}
                    </SheetTitle>
                    <SheetDescription>
                      {editingItem ? `Update ${activeConfig.entityName.toLowerCase()} details` : `Create a new ${activeConfig.entityName.toLowerCase()} for dropdown menus`}
                    </SheetDescription>
                  </SheetHeader>
                  <form id="master-data-form" onSubmit={handleSubmit} className="py-4 space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700">{activeConfig.entityName} Name *</Label>
                      <Input
                        data-testid="master-data-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={activeConfig.placeholder}
                        required
                      />
                    </div>
                    {activeConfig.hasParentCategory && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700">Parent Category</Label>
                        <select
                          data-testid="parent-category-select"
                          value={formData.parent_category}
                          onChange={(e) => setFormData({ ...formData, parent_category: e.target.value })}
                          className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                        >
                          <option value="">No Parent (standalone)</option>
                          {(data['categories'] || []).map(cat => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500">Link this subcategory to a main equipment category</p>
                      </div>
                    )}
                    {activeConfig.hasExtra && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700">Days On</Label>
                            <Input type="number" min="1" data-testid="days-on-input" value={formData.days_on} onChange={(e) => setFormData({ ...formData, days_on: e.target.value })} placeholder="e.g., 14" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700">Days Off</Label>
                            <Input type="number" min="1" data-testid="days-off-input" value={formData.days_off} onChange={(e) => setFormData({ ...formData, days_off: e.target.value })} placeholder="e.g., 7" />
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">Leave Days On/Off blank for variable rotations</p>
                      </>
                    )}
                  </form>
                  <SheetFooter>
                    <SheetClose asChild><Button type="button" variant="outline">Cancel</Button></SheetClose>
                    <Button type="submit" form="master-data-form" data-testid="save-master-data-button" className="bg-amber-500 hover:bg-amber-600">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-mono text-xs uppercase pl-6">{activeConfig.entityName} Name</TableHead>
                    {activeConfig.hasParentCategory && <TableHead className="font-mono text-xs uppercase">Parent Category</TableHead>}
                    {activeConfig.hasExtra && <TableHead className="font-mono text-xs uppercase text-center">Days On</TableHead>}
                    {activeConfig.hasExtra && <TableHead className="font-mono text-xs uppercase text-center">Days Off</TableHead>}
                    <TableHead className="font-mono text-xs uppercase">Modified By</TableHead>
                    <TableHead className="font-mono text-xs uppercase w-[80px] text-right pr-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      data-testid={`${activeTab}-row-${item.id}`}
                      className="cursor-pointer hover:bg-slate-50 group"
                      onClick={(e) => handleRowClick(e, item, activeTab)}
                    >
                      <TableCell className="font-medium text-sm pl-6">{item.name}</TableCell>
                      {activeConfig.hasParentCategory && <TableCell className="text-sm text-slate-600">{item.parent_category || <span className="text-slate-400">-</span>}</TableCell>}
                      {activeConfig.hasExtra && <TableCell className="text-sm text-center tabular-nums">{item.days_on || '-'}</TableCell>}
                      {activeConfig.hasExtra && <TableCell className="text-sm text-center tabular-nums">{item.days_off || '-'}</TableCell>}
                      <TableCell><LastModifiedBadge createdBy={item.created_by} updatedBy={item.updated_by} updatedAt={item.updated_at} /></TableCell>
                      <TableCell className="pr-6">
                        <div className="flex items-center justify-end gap-1" data-actions-menu>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); handleEdit(item, activeTab); }}
                            data-testid={`edit-${activeTab}-${item.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 text-slate-500" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`actions-menu-${activeTab}-${item.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(item, activeTab)}>
                                <Pencil className="mr-2 h-4 w-4" />Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(item.id, activeTab)} className="text-red-600 focus:text-red-600">
                                <Trash2 className="mr-2 h-4 w-4" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {items.length === 0 && (
                <div className="text-center py-16 px-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                    <activeConfig.icon className="h-5 w-5 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">No {pluralize(activeConfig.entityName).toLowerCase()} yet</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Add {activeConfig.entityName}" to create your first entry</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <AuditTrailPanel open={auditOpen} onOpenChange={setAuditOpen} entityType="category" title="Master Data Activity Log" />
    </div>
  );
}
